import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';
import StudioPageShell from '../../components/studio/StudioPageShell';
import { getExperienceLabel } from '../../config/domainExperience';

/**
 * Studio entry for the legacy Form Builder (same APIs as /formBuilder) plus links to drafts/published lists.
 */
export default function StudioFormsPage() {
  return (
    <StudioPageShell
      title="Forms"
      subtitle={`${getExperienceLabel()} — visual form designer, drafts, and published designs.`}
      backTo="/studio"
      backLabel="Studio"
      maxWidth={880}
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <CardActionArea component={RouterLink} to="/studio/forms/design" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    display: 'flex',
                  })}
                >
                  <DescriptionOutlinedIcon fontSize="large" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Form designer</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drag-and-drop builder — same experience as the standalone form builder, opened from Studio.
                  </Typography>
                </Box>
                <ChevronRightIcon color="action" />
              </Stack>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <CardActionArea component={RouterLink} to="/forms/drafts" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.text.primary, 0.06),
                    color: 'text.secondary',
                    display: 'flex',
                  })}
                >
                  <EditNoteOutlinedIcon fontSize="large" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Drafts</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Open and edit saved draft designs.
                  </Typography>
                </Box>
                <ChevronRightIcon color="action" />
              </Stack>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <CardActionArea component={RouterLink} to="/forms/published" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    color: 'success.main',
                    display: 'flex',
                  })}
                >
                  <PublishedWithChangesOutlinedIcon fontSize="large" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Published</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage published forms and links.
                  </Typography>
                </Box>
                <ChevronRightIcon color="action" />
              </Stack>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
    </StudioPageShell>
  );
}
